import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Storage } from '@/infra/storage'
import { createObjectStorage, type ObjectStorage } from '@/infra/object-storage'
import {
  allColumnIds,
  type ColumnId,
} from '@/features/jobs/list/jobs-table-columns'
import { type JobsTableColumnsViewModel } from '@/features/jobs/list/JobsTableColumnsViewModel'
import { type JobSearchViewModel } from '@/features/jobs/list/JobSearchViewModel'
import { JobListTabsViewModel } from '@/features/jobs/list/JobListTabsViewModel'
import { sharedSavedFilterId } from '@/features/jobs/list/saved-job-filters'
import { type SearchableJobSortField } from '@/features/jobs/list/job-sort-fields'
import { type CSVData, csvDownload } from '@/utils/csv-util'
import { type JobListPerspective } from '@/features/jobs/list/job-list-view'
import {
  type AnyJobListColumnId,
  type JobListColumn,
  jobListColumns,
  moveColumnId,
  orderColumns,
} from './job-list-columns'

interface StoredSettings {
  savedFilterId?: string
  displaySection?: DisplaySection
  visible?: Partial<Record<AnyJobListColumnId, boolean>>
  widths?: Partial<Record<AnyJobListColumnId, number>>
  order?: AnyJobListColumnId[]
}

/**
 * The lists the Display menu switches between.
 */
export type DisplaySection = 'tabs' | 'columns'

const minColumnWidth = 72
const maxColumnWidth = 720

/**
 * The My Jobs list: saved filter tabs, user-configurable columns,
 * and CSV export with a column picker.
 */
export class JobListPageViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _storage: ObjectStorage

  /**
   * Columns the user showed or hid, by ID.
   */
  @observable accessor columnVisibility: Partial<
    Record<AnyJobListColumnId, boolean>
  > = {}

  /**
   * Column widths the user set, by ID.
   */
  @observable accessor widths: Partial<Record<AnyJobListColumnId, number>> = {}

  /**
   * Column IDs in the order the user arranged them.
   */
  @observable accessor columnOrder: AnyJobListColumnId[] = []
  /**
   * Which list the Display menu shows, remembered between visits.
   */
  @observable accessor displaySection: DisplaySection = 'tabs'

  /**
   * Whether the user turned on the selection checkboxes.
   */
  @observable accessor isSelecting = false
  /**
   * Columns picked for the export.
   */
  @observable accessor exportSelection = new Set<AnyJobListColumnId>()
  /**
   * The export dialog.
   */
  readonly exportModal = new ModalViewModel()
  readonly tabsVm: JobListTabsViewModel

  constructor(
    readonly searcher: JobSearchViewModel,
    private readonly _columnsVm: JobsTableColumnsViewModel,
    storage: Storage,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    getViewerName: () => string,
    getShareUrl: (query: string) => string,
    writeToClipboard: (text: string) => Promise<void>,
  ) {
    this._storage = createObjectStorage(storage)
    this.tabsVm = new JobListTabsViewModel(
      searcher,
      this.selectSavedFilter,
      this._selectRememberedTab,
      getViewerName,
      getShareUrl,
      writeToClipboard,
      _newAlert,
    )
    this._lifecycle.manage(this.tabsVm)
  }

  /**
   * Whose list this is, which decides column headers and sorting.
   */
  @computed
  get perspective(): JobListPerspective {
    return this.searcher.listPerspective
  }

  /**
   * Columns this user is allowed to see, whether shown or not.
   */
  @computed
  get availableColumns(): JobListColumn[] {
    const legacyAccessible = new Set(
      this._columnsVm.filteredColumns.map((c) => c.id),
    )
    return jobListColumns.filter(
      (c) =>
        !allColumnIds.includes(c.id as ColumnId) ||
        legacyAccessible.has(c.id as ColumnId),
    )
  }

  /**
   * Columns the table can show.
   */
  @computed
  get tableColumns(): JobListColumn[] {
    return orderColumns(
      this.availableColumns.filter((c) => !c.exportOnly),
      this.columnOrder,
    )
  }

  /**
   * Columns the table shows.
   */
  @computed
  get visibleColumns(): JobListColumn[] {
    return this.tableColumns.filter((c) => this.isVisible(c))
  }

  /**
   * Columns offered in the export that the table doesn't show.
   */
  @computed
  get hiddenColumns(): JobListColumn[] {
    const visible = this.visibleColumns
    return this.availableColumns.filter((c) => !visible.includes(c))
  }

  /**
   * How many columns the export includes.
   */
  @computed
  get exportColumnCount() {
    return this.availableColumns.filter((c) => this.exportSelection.has(c.id))
      .length
  }

  /**
   * Whether rows can be selected for bulk actions.
   */
  @computed
  get canSelect(): boolean {
    return this._columnsVm.canSelectJobsForActions()
  }

  /**
   * Whether the selection checkboxes are shown.
   */
  @computed
  get showSelection(): boolean {
    return this.canSelect && this.isSelecting
  }

  /**
   * Whether the list only shows the user's own jobs.
   */
  @computed
  get isScopedToMine() {
    return this.searcher.scope === 'Mine'
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    this._load()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Whether the table shows the column.
   */
  isVisible(column: JobListColumn) {
    return (
      column.locked ||
      (this.columnVisibility[column.id] ?? column.defaultVisible)
    )
  }

  /**
   * The column's width.
   */
  widthOf(column: JobListColumn) {
    return this.widths[column.id] ?? column.defaultWidth
  }

  /**
   * The direction the list is sorted by the column, or `null` when it isn't.
   */
  sortDirectionOf(column: JobListColumn): 'asc' | 'desc' | null {
    const sorting = this.searcher.sorting
    if (
      sorting.type !== 'Field' ||
      sorting.field !== column.sortField?.(this.perspective)
    ) {
      return null
    }

    return sorting.direction === 'desc' ? 'desc' : 'asc'
  }

  /**
   * Cycles a column's sort: ascending, descending, then back to the default.
   */
  @action.bound
  toggleSort(column: JobListColumn) {
    if (!column.sortField) {
      return
    }
    const field: SearchableJobSortField = column.sortField(this.perspective)
    const direction = this.sortDirectionOf(column)
    if (direction === null) {
      this.searcher.setSortMethod({
        type: 'Field',
        field,
        direction: 'asc',
      })
    } else if (direction === 'asc') {
      this.searcher.setSortDirection('desc')
    } else {
      this.searcher.setSortMethod({ type: 'Default' })
    }
  }

  /**
   * Selects a saved filter tab and remembers it.
   */
  @action.bound
  selectSavedFilter(id: string) {
    this.searcher.selectSavedFilter(id)
    this.save()
  }

  /**
   * Widens the list to every job the user can see.
   */
  @action.bound
  showAllJobs() {
    this.searcher.setScope('All')
  }

  /**
   * Shows or hides a column.
   */
  @action.bound
  toggleColumn(id: AnyJobListColumnId) {
    const column = this.tableColumns.find((c) => c.id === id)
    if (!column || column.locked) {
      return
    }
    this.columnVisibility[column.id] = !this.isVisible(column)
    this.save()
  }

  /**
   * Moves a column to an index among the shown columns, as the table
   * shows them.
   */
  @action.bound
  moveColumn(id: string, toIndex: number) {
    this._moveColumn(this.visibleColumns, id, toIndex)
  }

  /**
   * Moves a column to an index among all table columns, shown or not, as the
   * Display menu lists them.
   */
  @action.bound
  moveListedColumn(id: string, toIndex: number) {
    this._moveColumn(this.tableColumns, id, toIndex)
  }

  /**
   * Sets a column's width within the allowed range.
   */
  @action.bound
  setWidth(id: string, width: number) {
    this.widths[id as AnyJobListColumnId] = Math.round(
      Math.min(maxColumnWidth, Math.max(minColumnWidth, width)),
    )
  }

  @action.bound
  setDisplaySection(section: DisplaySection) {
    this.displaySection = section
    this.save()
  }

  /**
   * Shows or hides the selection checkboxes.
   */
  @action.bound
  toggleSelecting() {
    this.isSelecting = !this.isSelecting
  }

  /**
   * Restores the default columns, widths and order.
   */
  @action.bound
  resetColumns() {
    this.columnVisibility = {}
    this.widths = {}
    this.columnOrder = []
    this.save()
  }

  /**
   * Opens the export dialog with the shown columns picked.
   */
  @action.bound
  openExportDialog() {
    this.exportSelection = new Set(this.visibleColumns.map((c) => c.id))
    this.exportModal.show()
  }

  /**
   * Picks or unpicks a column for the export.
   */
  @action.bound
  toggleExportColumn(id: AnyJobListColumnId) {
    if (this.exportSelection.has(id)) {
      this.exportSelection.delete(id)
    } else {
      this.exportSelection.add(id)
    }
  }

  /**
   * Picks or unpicks every shown column for the export.
   */
  @action.bound
  setVisibleColumnsExported(selected: boolean) {
    this._setColumnsExported(this.visibleColumns, selected)
  }

  /**
   * Picks or unpicks every hidden column for the export.
   */
  @action.bound
  setHiddenColumnsExported(selected: boolean) {
    this._setColumnsExported(this.hiddenColumns, selected)
  }

  /**
   * Downloads the filtered jobs with the picked columns as a CSV file.
   */
  exportCsv = task.resolved(async () => {
    const columns = this.availableColumns.filter((c) =>
      this.exportSelection.has(c.id),
    )
    if (!columns.length) {
      return
    }

    const progress = this._newAlert().saying('Exporting jobs…').show()
    const jobs = await this.searcher
      .getAllFilteredJobs()
      .catch((err: unknown) => {
        progress.dismiss()
        return this._createAlertErrorHandler('Exporting jobs failed')(err)
      })
    const csv: CSVData = [
      columns.map((c) => c.header(this.perspective)),
      ...jobs.map((job) =>
        columns.map((c) => c.exportValue(job, this.perspective)),
      ),
    ]
    csvDownload(csv, 'jobs.csv')
    progress
      .withState('success')
      .saying(`Exported ${jobs.length} jobs`)
      .autoDismiss()
    this.exportModal.close()
  })

  /**
   * Remembers the selected tab and column settings.
   */
  @action.bound
  save() {
    const id = this.searcher.savedFilterId
    const settings: StoredSettings = {
      // A shared tab isn't kept, so the tab picked before it stays
      // remembered.
      savedFilterId:
        id === sharedSavedFilterId
          ? (this._storage.getItem(this._storageKey) as StoredSettings | null)
              ?.savedFilterId
          : (id ?? undefined),
      visible: this.columnVisibility,
      widths: this.widths,
      displaySection: this.displaySection,
      order: this.columnOrder,
    }
    this._storage.setItem(this._storageKey, settings)
  }

  private get _storageKey() {
    return `JobList Settings - ${this.searcher.listPerspective}`
  }

  @action.bound
  private _load() {
    const stored = (this._storage.getItem(this._storageKey) ??
      {}) as StoredSettings
    this.columnVisibility = stored.visible ?? {}
    this.widths = stored.widths ?? {}
    this.displaySection = stored.displaySection ?? 'tabs'
    this.columnOrder = stored.order ?? []
    if (this.searcher.savedFilterId === null) {
      this.searcher.selectSavedFilter(stored.savedFilterId)
    }
  }

  @action.bound
  private _selectRememberedTab() {
    const stored = this._storage.getItem(
      this._storageKey,
    ) as StoredSettings | null
    this.searcher.selectSavedFilter(stored?.savedFilterId)
  }

  private _moveColumn(among: JobListColumn[], id: string, toIndex: number) {
    const column = among.find((c) => c.id === id)
    if (!column || column.locked) {
      return
    }
    const first = among.filter((c) => c.locked).length
    this.columnOrder = moveColumnId(
      this.tableColumns.map((c) => c.id),
      among.map((c) => c.id),
      column.id,
      Math.min(among.length - 1, Math.max(first, toIndex)),
    )
    this.save()
  }

  private _setColumnsExported(columns: JobListColumn[], selected: boolean) {
    columns.forEach((c) =>
      selected
        ? this.exportSelection.add(c.id)
        : this.exportSelection.delete(c.id),
    )
  }
}
